import React, { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { I } from '../icons-ds';
import { Button, Input, Checkbox } from '../ui';
import { useViewport } from '../layout';
import { useTheme } from '../lib/ThemeContext';
import { LAW_CARDS, type LawCard } from './lawCards';
import './login-lux.css';

/**
 * One of the six lawsofux.com card illustrations, chosen at random per visit
 * (never the same one twice in a row). The card's original background color is
 * exported to login-lux.css as --lux-card-accent, so the whole screen — meta
 * label, button, checkbox, logo — re-themes to match. Shapes stagger in
 * exactly like the site's cards (inline --animation-order, see login-lux.css).
 */
const CARD_KEY = 'dp-login-card';

function pickCard(): LawCard {
  let last = -1;
  try {
    const forced = window.localStorage.getItem('dp-login-card-force');
    if (forced != null && LAW_CARDS[Number(forced)]) return LAW_CARDS[Number(forced)];
    last = Number(window.localStorage.getItem(CARD_KEY));
  } catch (e) { /* ignore */ }
  const pool = LAW_CARDS.map((_, i) => i).filter((i) => i !== last);
  const idx = pool[Math.floor(Math.random() * pool.length)] ?? 0;
  try { window.localStorage.setItem(CARD_KEY, String(idx)); } catch (e) { /* ignore */ }
  return LAW_CARDS[idx];
}

/* The site sizes each illustration at 85% of its card; we breathe a touch
   more (78%) per user preference. Inset, not padding, so the gutter scales
   with the card at every size. */
const LawCardArt = ({ card, size }: { card: LawCard; size?: number }) => (
  <div
    role="img"
    aria-label={`${card.title} illustration from Laws of UX`}
    style={{
      position: 'relative',
      width: size ?? '100%',
      maxWidth: size ?? 440,
      aspectRatio: '1',
      background: card.bg,
      borderRadius: size ? 5 : 8,
      flex: 'none',
    }}
  >
    <div
      className="lux-card-art"
      style={{ position: 'absolute', inset: '11%' }}
      dangerouslySetInnerHTML={{ __html: card.svg }}
    />
  </div>
);

export function LoginScreen() {
  const viewport = useViewport();
  const navigate = useNavigate();
  const location = useLocation();
  const { setPalette, setAccent } = useTheme();
  const isMobile = viewport === 'mobile';

  // Where RequireAuth bounced us from, if anywhere. Same-origin paths only —
  // the state is ours, but a bare '/' prefix check keeps it from ever becoming
  // an open redirect.
  const from = (location.state as { from?: string } | null)?.from;
  const dest = from && from.startsWith('/') && !from.startsWith('//') ? from : '/search';

  const [card] = useState(pickCard);
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/.netlify/functions/authenticate', {
        method: 'POST',
        body: JSON.stringify({ username: user, password: pass }),
      });
      const data = await response.json();
      if (data.isAuthenticated) {
        localStorage.setItem('isAuthenticated', 'true');
        // Carry the landing-page theme into the app. Settings can still switch
        // to any other palette afterwards; the next sign-in re-applies this one.
        setPalette('lawsofux');
        setAccent('auto'); // show the palette's steel accent, not a stale override
        navigate(dest, { replace: true });
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('Authentication failed. Please try again.');
      // eslint-disable-next-line no-console
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const logoRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <I.logo size={32} />
      <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.3px' }}>DotPhrase</span>
    </div>
  );

  return (
    <div
      className="lux-login"
      style={{
        minHeight: '100dvh', width: '100%',
        background: 'var(--bg-canvas)', color: 'var(--fg-primary)',
        display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'stretch',
        ...({ '--lux-card-accent': card.bg } as React.CSSProperties),
      }}
    >
      {isMobile ? (
        <div
          className="lux-login__art"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16, padding: '18px 20px',
            paddingTop: 'max(18px, env(safe-area-inset-top))',
          }}
        >
          {logoRow}
          <LawCardArt card={card} size={52} />
        </div>
      ) : (
        <div
          className="lux-login__art"
          style={{
            flex: 1, minWidth: 360,
            padding: viewport === 'tablet' ? 32 : 48,
            display: 'flex', flexDirection: 'column',
          }}
        >
          {logoRow}

          <div
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '32px 0',
            }}
          >
            <LawCardArt card={card} />
          </div>

          <div className="lux-login__meta" style={{ color: 'hsl(60, 6%, 58%)' }}>
            Maintained by Samuel Edusa, MD
          </div>
        </div>
      )}

      <div
        style={{
          flex: isMobile ? 1 : '0 0 480px',
          display: 'flex', flexDirection: 'column',
          padding: isMobile ? '32px 24px' : '48px 56px',
          background: 'var(--bg-surface)',
        }}
      >
        <div
          style={{
            flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
            maxWidth: 360, margin: isMobile ? '0 auto' : 0, width: '100%',
          }}
        >
          <div className="lux-login__meta" style={{ color: 'var(--accent)', marginBottom: 14 }}>
            Clinical phrase library
          </div>
          <div className="t-title1" style={{ marginBottom: 8 }}>Welcome back</div>
          <div className="t-subhead" style={{ color: 'var(--fg-secondary)', marginBottom: 28 }}>
            Sign in to your phrase library
          </div>

          {error && (
            <div
              role="alert"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 12px', marginBottom: 14,
                borderRadius: 'var(--r-md)',
                background: 'var(--danger-fill)', color: 'var(--danger)',
                fontSize: 14, fontWeight: 500,
              }}
            >
              <I.warning size={16} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input
              label="Username"
              leading={<I.user size={16} />}
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="username"
              autoComplete="username"
              size="lg"
              required
              disabled={loading}
            />
            <Input
              label="Password"
              leading={<I.lock size={16} />}
              trailing={
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? 'Hide password' : 'Show password'}
                  style={{
                    color: 'var(--fg-tertiary)', display: 'inline-flex',
                    background: 'transparent', border: 0, cursor: 'pointer', padding: 0,
                  }}
                >
                  {show ? <I.eyeOff size={16} /> : <I.eye size={16} />}
                </button>
              }
              type={show ? 'text' : 'password'}
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              size="lg"
              required
              disabled={loading}
            />

            <label
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                cursor: 'pointer', marginTop: 4, alignSelf: 'flex-start',
              }}
            >
              <Checkbox checked={remember} onChange={() => setRemember((r) => !r)} />
              <span className="t-subhead">Stay signed in</span>
            </label>

            <Button
              variant="primary"
              size="lg"
              full
              type="submit"
              loading={loading}
              style={{ marginTop: 12 }}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>

        <div
          className="lux-login__meta"
          style={{ color: 'var(--fg-tertiary)', textAlign: 'center', marginTop: 32 }}
        >
          v2.0
        </div>
      </div>
    </div>
  );
}

export default LoginScreen;
